<script setup lang="ts">
import { ref } from "vue";
import { DateTime } from "luxon";
import { useI18n } from "vue-i18n";

import { CALENDAR_VIEW, CALENDAR_VIEW_OPTIONS, SELECTED_DATE_FORMAT } from "../constants";
import type { CalendarApp } from "@schedule-x/calendar";
import type { CalendarControl } from "../Plugins/CalendarControl";

import useAppStore from "@/Modules/App/Store";

import Button from "@/Components/UI/TcButton.vue";
import TcIcon from "@/Components/Extend/TcIcon.vue";
import Select, { type Option } from "@/Components/UI/TcSelect.vue";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

import { dateTimeToPlainDate, plainDateToDateTime } from "../utils";

export interface HeaderProps {
  title?: string;
  showDate?: string;
  app: CalendarApp;
  isMobile?: boolean;
  controls: CalendarControl;
}

const { t } = useI18n();
const appStore = useAppStore();

const props = withDefaults(defineProps<HeaderProps>(), {
  showDate: DateTime.now().toFormat(SELECTED_DATE_FORMAT),
});
const emit = defineEmits<{
  (event: "changeView", value: CALENDAR_VIEW): void;
  (event: "addEvent"): void;
  (event: "nextDate", value: DateTime): void;
  (event: "prevDate", value: DateTime): void;
}>();

const selectedView = ref<Option>();

const getCurrentCalendarView = (): CALENDAR_VIEW => props.controls.getView() as CALENDAR_VIEW;
const getCalendarViewOption = (): Option =>
  CALENDAR_VIEW_OPTIONS.find((op) => (op.value as CALENDAR_VIEW) === getCurrentCalendarView())!;

const updateCalendarView = (option?: Option) => {
  selectedView.value = option!;

  emit("changeView", option!.value as CALENDAR_VIEW);
};

const getRangeDateTime = () => {
  const range = props.controls.getRange();

  if (!hasValue(range)) return;

  return range;
};

const getHeaderTitle = () => {
  const range = getRangeDateTime();

  if (!hasValue(range)) return;

  const { start, end } = range;
  const currentView = getCurrentCalendarView();
  const currentDateTime = isValidString(props.showDate)
    ? DateTime.fromFormat(props.showDate, SELECTED_DATE_FORMAT)
    : DateTime.now();

  if (appStore.isMobileViewport) {
    if (currentView === CALENDAR_VIEW.WEEK) {
      return `${t(`schedule.calendar.months.${start.month - 1}`)} ${start.year}`;
    } else if (currentView === CALENDAR_VIEW.DAY) {
      return t("schedule.calendar.day.mobile.title", {
        day: start.day,
        month: t(`schedule.calendar.months.${start.month - 1}`),
        year: start.year,
      });
    }
  }

  if (currentView === CALENDAR_VIEW.MONTH) {
    return `${t(`schedule.calendar.months.${currentDateTime.month - 1}`)} ${currentDateTime.year}`;
  } else if (currentView === CALENDAR_VIEW.WEEK) {
    const startMonth = start.month;
    const endMonth = end.month;

    if (startMonth !== endMonth) {
      return `${t(`schedule.calendar.months.${startMonth - 1}`)} - ${t(`schedule.calendar.months.${endMonth - 1}`)} ${start.year}`;
    } else {
      return `${t(`schedule.calendar.months.${startMonth - 1}`)} ${start.year}`;
    }
  } else {
    return `${t(`schedule.calendar.months.${start.month - 1}`)} ${start.year}`;
  }
};

const handleNextPeriod = () => {
  let currentDateTime = DateTime.fromFormat(props.showDate, SELECTED_DATE_FORMAT);
  const currentView = getCurrentCalendarView();

  if (currentView === CALENDAR_VIEW.DAY) {
    currentDateTime = currentDateTime.plus({ day: 1 });
  } else if (currentView === CALENDAR_VIEW.WEEK) {
    currentDateTime = currentDateTime.plus({ week: 1 });
  } else if (currentView === CALENDAR_VIEW.MONTH) {
    currentDateTime = currentDateTime.plus({ month: 1 });
  }

  props.controls.setDate(dateTimeToPlainDate(currentDateTime));

  const range = props.controls.getRange();

  if (hasValue(range)) {
    currentDateTime = plainDateToDateTime(range.end.toPlainDate());
  }

  emit("nextDate", currentDateTime);
};

const handlePrevPeriod = () => {
  let currentDateTime = DateTime.fromFormat(props.showDate, SELECTED_DATE_FORMAT);
  const currentView = getCurrentCalendarView();

  if (currentView === CALENDAR_VIEW.DAY) {
    currentDateTime = currentDateTime.minus({ day: 1 });
  } else if (currentView === CALENDAR_VIEW.WEEK) {
    currentDateTime = currentDateTime.minus({ week: 1 });
  } else if (currentView === CALENDAR_VIEW.MONTH) {
    currentDateTime = currentDateTime.minus({ month: 1 });
  }

  props.controls.setDate(dateTimeToPlainDate(currentDateTime));

  const range = props.controls.getRange();

  if (hasValue(range)) {
    currentDateTime = plainDateToDateTime(range.start.toPlainDate());
  }

  emit("prevDate", currentDateTime);
};

const handleAddEvent = () => {
  emit("addEvent");
};
</script>

<template>
  <div
    class="header"
    :class="{
      'header--week': selectedView?.value === CALENDAR_VIEW.WEEK,
      'header--day': selectedView?.value === CALENDAR_VIEW.DAY,
      'header--month': selectedView?.value === CALENDAR_VIEW.MONTH,
    }"
  >
    <div class="header__prev">
      <TcIcon name="ChevronLeft" @click="handlePrevPeriod" />
    </div>

    <div class="header__next">
      <TcIcon name="ChevronRight" @click="handleNextPeriod" />
    </div>

    <div class="header__title">
      {{ getHeaderTitle() }}
    </div>

    <div class="header__select_view">
      <Select
        :modelValue="getCalendarViewOption()"
        name="calendar_view"
        class="header__select_view__select"
        isSmall
        :options="
          CALENDAR_VIEW_OPTIONS.map((op) => ({ ...op, label: t(`schedule.calendar.view.${op.label}`) }))
        "
        :gutterBottom="false"
        @change="updateCalendarView"
      />
    </div>

    <div class="header__add">
      <Button isSmall class="header__add__button" @click="handleAddEvent">
        <TcIcon name="Plus" color="background-alt" class="header__add__button__icon" />
        {{ t("calendars.view.header.add_event") }}
      </Button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.header {
  @include sp.padding(y, 3);
  @include sp.padding(x, 4);
  @include border(1, solid, b);
  @include colors.border(content, low-solid);

  display: flex;
  height: $size-bar-height-xs;
  align-items: center;
  flex-direction: row;
  width: 100%;
  gap: $space-3;

  & > * {
    display: flex;
    flex-grow: 0;

    &:last-child {
      margin-right: 0;
    }
  }

  &__next,
  &__prev {
    cursor: pointer;
  }

  &__title {
    @include text.general(high, m, true);
    @include colors.color(content, high);

    flex-grow: 2;
    display: block;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  &__select_view {
    width: 180px;

    &__select {
      display: contents !important;
    }
  }
}

@media (min-width: $breakpoint-m) {
  .header {
    height: $size-bar-height-md;
  }
}

@media (max-width: $breakpoint-m) {
  .header {
    &--week {
      .header {
        &__prev {
          display: flex;
          order: 1;
        }

        &__title {
          display: flex;
          flex-grow: 0;
          order: 2;
          margin: 0;
        }

        &__next {
          display: flex;
          margin-left: 1.25rem;
          order: 3;
        }

        &__add {
          display: flex;
          order: 4;
        }
      }
    }

    &--month,
    &--week,
    &--day {
      .header {
        &__prev {
          order: 0;
          flex-grow: 1;
          justify-content: flex-start;
        }

        &__next {
          order: 2;
          flex-grow: 2;
          justify-content: flex-start;
        }

        &__title {
          order: 1;
          flex-grow: 1;
          margin: 0;
          text-align: center;
          display: flex;
          flex-direction: column;
        }

        &__select_view {
          order: 3;
          display: flex;
          flex-grow: 1;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.header {
  &__add {
    cursor: pointer;

    &__button {
      @include colors.bg(primary, high, true);
      @include colors.border(primary, high, true);
    }

    .btn__content {
      @include text.general(high, s);

      display: flex;
      gap: $space-2;
    }
  }
}

@media (max-width: $breakpoint-m) {
  .header {
    &__add {
      display: none !important;
    }

    &--month {
      .header {
        &__title {
          & > span {
            @include colors.color(content, mid);

            font-weight: 400;
            font-family: $ts-figtree-regular-font;
            font-size: 0.75rem;
            line-height: $space-4;

            &:first-child {
              @include colors.color(content, high);

              font-size: 1.25rem;
              line-height: 1.75rem;
              font-family: $ts-figtree-bold-font;
              text-transform: capitalize;
            }
          }
        }
      }
    }
  }
}
</style>
